import React from "react";
import { ArrowUpRight } from "lucide-react";

/**
 * Single service tile.
 * Default view: icon + title + description on cream.
 * If `service.image` is provided, the photo reveals as background on hover/focus/active.
 */
const ServiceCard = ({ service, index, onSelect }) => {
  const { icon: Icon, title, desc, image } = service;
  const hasImage = Boolean(image);

  return (
    <button
      data-testid={`service-card-${index}`}
      onClick={() => onSelect(title)}
      className="group text-left border-r border-b border-[#0A0A0A] hover:bg-[#FFF44F] transition-colors relative overflow-hidden min-h-[180px] md:min-h-[260px]"
    >
      {/* Image layer (revealed on hover/focus) */}
      {hasImage && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 group-focus:opacity-100 group-active:opacity-100 transition-opacity duration-500 z-0">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
        </div>
      )}

      {/* Content layer */}
      <div
        className={`relative z-10 p-4 md:p-8 transition-colors ${
          hasImage ? "group-hover:text-white" : ""
        }`}
      >
        <div className="flex items-start justify-between mb-4 md:mb-6">
          <div
            className={`w-9 h-9 md:w-12 md:h-12 border-2 flex items-center justify-center transition-colors ${
              hasImage
                ? "border-[#0A0A0A] bg-[#FFF44F] text-[#0A0A0A] group-hover:bg-[#FFF44F] group-hover:border-[#FFF44F] group-hover:text-[#0A0A0A]"
                : "border-[#0A0A0A] bg-[#FFF44F] text-[#0A0A0A] group-hover:bg-[#0A0A0A] group-hover:text-[#FFF44F]"
            }`}
          >
            <Icon size={16} className="md:hidden" />
            <Icon size={20} className="hidden md:block" />
          </div>
          <ArrowUpRight
            size={16}
            className={`md:hidden transition-all ${
              hasImage
                ? "text-[#9A9A9A] group-hover:text-[#FFF44F]"
                : "text-[#9A9A9A] group-hover:text-[#0A0A0A]"
            }`}
          />
          <ArrowUpRight
            size={20}
            className={`hidden md:block transition-all ${
              hasImage
                ? "text-[#9A9A9A] group-hover:text-[#FFF44F] group-hover:translate-x-1 group-hover:-translate-y-1"
                : "text-[#9A9A9A] group-hover:text-[#0A0A0A] group-hover:translate-x-1 group-hover:-translate-y-1"
            }`}
          />
        </div>
        <h3
          className={`font-display text-sm md:text-2xl mb-2 md:mb-3 leading-tight transition-colors ${
            hasImage ? "text-[#0A0A0A] group-hover:text-white" : "text-[#0A0A0A]"
          }`}
        >
          {title}
        </h3>
        <p
          className={`hidden md:block text-sm leading-relaxed transition-colors ${
            hasImage ? "text-[#3A3A3A] group-hover:text-zinc-100" : "text-[#3A3A3A]"
          }`}
        >
          {desc}
        </p>
        {hasImage && (
          <p className="font-mono-tag text-[9px] md:text-[10px] text-[#FFF44F] mt-2 md:mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            ▸ PROYECTO REAL
          </p>
        )}
      </div>
    </button>
  );
};

export default ServiceCard;
